import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { CropObject } from "shared/types/media";

import { Modal, ModalFooter, ModalText } from "shared/components/Modals/Modal";
import PhotoCrop from "shared/components/Modals/UploadPhotoModal/PhotoCrop";

import { simplyClick } from "shared/helpers/analytics";

import classesModal from "@platformAssets/components/Modal.module.scss";
import classes from "@platformAssets/components/UploadMediaNew.module.scss";

import { ReactComponent as RotateIcon } from "assets/shared/img/rotate.svg";

const aspect = 139 / 239;

const initPix = {
  width: 0,
  height: 0,
  x: 0,
  y: 0,
};

type CropModalProps = {
  analytics?: Record<string, string[]>;
  close: () => void;
  cropSrc: string;
  initImg: string;
  setCropData: (data: CropObject) => void;
  setCropSrc: (preview: string) => void;
  setFileLoaded: (data: boolean) => void;
  successAction: (data: CropObject) => void;
};

const CropModal: React.FC<CropModalProps> = ({
  close,
  successAction,
  cropSrc,
  setCropSrc,
  setCropData,
  initImg,
  analytics,
  setFileLoaded,
}): JSX.Element => {
  const { t } = useTranslation("uploadMedia");
  const imageRef = useRef<HTMLImageElement>(null);
  const rotateRef = useRef<HTMLCanvasElement>(null);
  const hiddenImg = useRef<HTMLImageElement>(null);
  const [rotate, setRotate] = useState(0);
  const [crop, setCrop] = useState<object | CropObject>({});
  const [pix, setPix] = useState(initPix);

  /**
   * Rotate current image
   */
  const rotateImg = (): void => {
    analytics?.cropRotate?.forEach((item: string) => {
      simplyClick(item, null, false);
    });

    const canvas = rotateRef.current;
    const image = hiddenImg.current;

    if (!canvas || typeof canvas.getContext !== "function" || !image) {
      return;
    }

    const ctx = canvas.getContext("2d");

    if (!ctx) {
      return;
    }

    let newAngle = rotate + 90;

    if (newAngle > 360) {
      newAngle = 90;
    }

    let width = image.naturalWidth;
    let height = image.naturalHeight;

    if ([90, 270].includes(newAngle)) {
      width = image.naturalHeight;
      height = image.naturalWidth;
    }

    canvas.width = width;
    canvas.height = height;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.translate(width / 2, height / 2);
    ctx.rotate((newAngle * Math.PI) / 180);

    ctx.drawImage(image, -image.naturalWidth / 2, -image.naturalHeight / 2);

    setRotate(newAngle);
    setCropSrc(canvas.toDataURL());
  };

  /**
   * Click next step handler
   */
  const handleClickNext = (): void => {
    const cropData = {
      crop,
      pix,
      rotate,
    };

    analytics?.cropNext?.forEach((item: string) => {
      simplyClick(item, null, false);
    });

    setCropData(cropData);

    resetComponentData();
    successAction(cropData);
  };

  /**
   * Reset modal crop data
   */
  const resetComponentData = (): void => {
    setPix(initPix);
    setCrop({});
    setFileLoaded(false);
    setRotate(0);
  };

  return (
    <Modal
      close={close}
      bodyClass={cn(classesModal.mModal, classes.modalCrop)}
      isAnimated
    >
      <ModalText text={t("cropImage.title")} isTitle={true} />
      <ModalText text={t("cropImage.description")} />
      <PhotoCrop
        cropSrc={cropSrc}
        index={0}
        setImageLoaded={setFileLoaded}
        imgAngle={rotate}
        aspect={aspect}
        crop={crop}
        setCrop={setCrop}
        pix={pix}
        setPix={setPix}
        imageRef={imageRef}
        hiddenImg={hiddenImg}
        rotateRef={rotateRef}
        setCropData={setCropData}
        className={classes.crop}
      />
      {initImg && (
        <img
          ref={hiddenImg}
          alt={"hidden-crop-img"}
          src={initImg}
          className={classes.imgCrop}
        />
      )}
      <ModalFooter customClass={cn(classesModal.footerM, classes.footerCrop)}>
        <div className={cn(classes.buttonCrop, classes.buttonLinkCrop)}>
          <button
            className={cn(classes.button, classes.secondButton)}
            onClick={rotateImg}
          >
            <RotateIcon />
            {t("cropImage.rotate")}
          </button>
        </div>
        <button
          className={cn(
            classes.button,
            classes.firstButton,
            classes.buttonCrop
          )}
          onClick={handleClickNext}
        >
          {t("common:btnText.Next")}
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default CropModal;
